import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { 
  CheckCircle, ShoppingCart, Truck, Calendar, MapPin, 
  DollarSign, FileText, Mail, Phone, ArrowLeft, Copy, 
  Check, Download, Printer, Share2, AlertTriangle, 
  CreditCard, Building, Globe, Send, Shield, Landmark
} from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { supabase } from '../lib/supabase';
import { useEmail } from '../hooks/useEmail';

interface OrderDetails {
  id: string;
  order_number: string;
  total_amount: number;
  status: string;
  payment_status: string;
  payment_method: string;
  shipping_address: string;
  shipping_city: string;
  shipping_country: string;
  shipping_postal_code: string;
  shipping_method: string;
  tracking_number: string | null;
  expected_delivery: string | null;
  created_at: string;
  items: OrderItem[];
}

interface OrderItem {
  id: string;
  product_id: string;
  quantity: number;
  price: number;
  total_price: number;
  product: {
    name: string;
    image_url: string | null;
    supplier: {
      organization: string;
    } | null;
  } | null;
}

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const { user } = useSupabase();
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedOrderNumber, setCopiedOrderNumber] = useState(false);
  const { sendOrderConfirmation } = useEmail();

  // Get order details from location state or fetch from API
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        let currentOrder = null;
        
        // Check if order details are in location state
        if (location.state?.order) {
          currentOrder = location.state.order;
          setOrder(currentOrder);
          return;
        }
        
        // Fetch order details from API
        if (orderId) {
          const { data, error: orderError } = await supabase
            .from('orders')
            .select(`
              *,
              items:order_items(
                id,
                product_id,
                quantity,
                price,
                total_price,
                product:product_id(
                  name,
                  image_url,
                  supplier:supplier_id(organization)
                )
              )
            `)
            .eq('id', orderId)
            .single();
          
          if (orderError) throw orderError;
          
          currentOrder = data;
          setOrder(currentOrder);
        } else {
          throw new Error('Order ID not provided');
        }
        
        // Send order confirmation email if order data is available
        if (currentOrder) {
          // Email notifications are handled in production environment
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrderDetails();
  }, [orderId, location.state, user]);

  // Send order confirmation email
  const sendOrderConfirmationEmail = async (orderData: OrderDetails) => {
    if (!user || !orderData) return;
    
    try {
      setSendingEmail(true);
      
      // Get user details
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('first_name, last_name, email')
        .eq('id', user.id)
        .single();
      
      if (userError || !userData) {
        console.error('Error fetching user details:', userError);
        return;
      }
      
      // Get order items
      const { data: orderItemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          quantity,
          price,
          total_price,
          product:product_id(name)
        `)
        .eq('order_id', orderData.id);
      
      if (itemsError || !orderItemsData) {
        console.error('Error fetching order items:', itemsError);
        return;
      }
      
    } catch (err) {
      console.error('Error sending order confirmation email:', err);
    } finally {
      setSendingEmail(false);
    }
  };

  // Copy order number to clipboard
  const copyOrderNumber = () => {
    if (order?.order_number) {
      navigator.clipboard.writeText(order.order_number);
      setCopiedOrderNumber(true);
      setTimeout(() => setCopiedOrderNumber(false), 2000);
    }
  };

  // Get payment method icon
  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card':
        return <CreditCard className="h-5 w-5" />;
      case 'western_union':
        return <Globe className="h-5 w-5" />;
      case 'moneygram':
        return <Send className="h-5 w-5" />;
      case 'bank_transfer':
        return <Building className="h-5 w-5" />;
      case 'escrow':
        return <Shield className="h-5 w-5" />;
      case 'letter_of_credit':
        return <Landmark className="h-5 w-5" />;
      default:
        return <DollarSign className="h-5 w-5" />;
    }
  };

  // Get payment method name
  const getPaymentMethodName = (method: string) => {
    switch (method) {
      case 'credit_card':
        return 'Credit/Debit Card';
      case 'western_union':
        return 'Western Union';
      case 'moneygram':
        return 'Moneygram';
      case 'bank_transfer':
        return 'Bank Transfer/TT';
      case 'escrow':
        return 'Escrow Service';
      case 'letter_of_credit':
        return 'Letter of Credit (LC)';
      default:
        return 'Unknown';
    }
  };

  // Get payment status badge
  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Paid
        </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Pending
        </span>;
      case 'refunded':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <DollarSign className="h-3 w-3 mr-1" />
          Refunded
        </span>;
      default:
        return null;
    }
  };

  // Get order status badge
  const getOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Pending
        </span>;
      case 'processing':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <ShoppingCart className="h-3 w-3 mr-1" />
          Processing
        </span>;
      case 'shipped':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <Truck className="h-3 w-3 mr-1" />
          Shipped
        </span>;
      case 'delivered':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Delivered
        </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Cancelled
        </span>;
      default:
        return null;
    }
  };

  // Mock order data for demo
  const mockOrder: OrderDetails = {
    id: 'mock-id',
    order_number: 'ORD-123456',
    total_amount: 75.97,
    status: 'pending',
    payment_status: 'pending',
    payment_method: 'bank_transfer',
    shipping_address: '123 Main St',
    shipping_city: 'New York',
    shipping_country: 'United States',
    shipping_postal_code: '10001',
    shipping_method: 'Standard Shipping',
    tracking_number: null,
    expected_delivery: null,
    created_at: new Date().toISOString(),
    items: [
      {
        id: 'item-1',
        product_id: 'product-1',
        quantity: 2,
        price: 29.99,
        total_price: 59.98,
        product: {
          name: 'Premium Cotton T-Shirt',
          image_url: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
          supplier: {
            organization: 'Bengal Textiles Ltd.'
          }
        }
      },
      {
        id: 'item-2',
        product_id: 'product-2',
        quantity: 1,
        price: 15.99,
        total_price: 15.99,
        product: {
          name: 'Natural Jute Shopping Bag',
          image_url: 'https://images.pexels.com/photos/5217911/pexels-photo-5217911.jpeg',
          supplier: {
            organization: 'Eco Jute Co.'
          }
        }
      }
    ]
  };

  // Use mock data if no order is loaded
  const displayOrder = order || mockOrder;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Order</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to="/"
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="mr-4 text-gray-500 hover:text-gray-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Order Confirmation</h1>
                <p className="text-gray-600">Thank you for your purchase</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => window.print()}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <Printer className="h-5 w-5" />
                <span className="hidden md:inline">Print</span>
              </button>
              <button
                onClick={copyOrderNumber}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                {copiedOrderNumber ? (
                  <>
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="hidden md:inline text-green-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5" />
                    <span className="hidden md:inline">Copy Order #</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Order Confirmation Message */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="text-center mb-6">
            <div className="bg-green-100 p-3 rounded-full inline-block mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Confirmed!</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Thank you for your order. We've received your order and will begin processing it shortly.
              {displayOrder.payment_status === 'pending' && ' Please complete your payment using the instructions below.'}
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-4 md:mb-0">
              <div className="mr-4">
                <div className="text-sm text-gray-600">Order Number</div>
                <div className="text-lg font-semibold text-gray-900">{displayOrder.order_number}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Order Date</div>
                <div className="text-lg font-semibold text-gray-900">
                  {new Date(displayOrder.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getOrderStatusBadge(displayOrder.status)}
              {getPaymentStatusBadge(displayOrder.payment_status)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Order Items</h2>
              
              <div className="space-y-6">
                {displayOrder.items.map((item) => (
                  <div key={item.id} className="flex items-start space-x-4">
                    <img 
                      src={item.product?.image_url || 'https://via.placeholder.com/80'} 
                      alt={item.product?.name || 'Product'} 
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{item.product?.name || 'Product'}</h3>
                      <p className="text-sm text-gray-600">{item.product?.supplier?.organization || 'Supplier'}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-gray-700">Qty: {item.quantity}</span>
                        <span className="text-sm font-medium text-gray-900">${item.total_price.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Payment Information</h2>
              
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-gray-100 rounded-lg">
                  {getPaymentMethodIcon(displayOrder.payment_method)}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{getPaymentMethodName(displayOrder.payment_method)}</div>
                  <div className="text-sm text-gray-600">
                    Status: {displayOrder.payment_status === 'paid' ? 'Paid' : 'Pending'}
                  </div>
                </div>
              </div>
              
              {/* Payment Instructions for non-credit card methods */}
              {displayOrder.payment_status === 'pending' && (
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <DollarSign className="h-5 w-5 text-yellow-600 mr-2" />
                    Payment Instructions
                  </h3>
                  
                  {displayOrder.payment_method === 'western_union' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-700">
                        Please send your payment via Western Union to the following recipient:
                      </p>
                      <div className="bg-white p-3 rounded-lg border border-yellow-200">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Recipient Name:</span>
                            <span>Made in BD Ltd.</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Country:</span>
                            <span>Bangladesh</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">City:</span>
                            <span>Dhaka</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Amount to Send:</span>
                            <span>${displayOrder.total_amount.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        After sending the payment, please email the Money Transfer Control Number (MTCN) to <a href="mailto:payments@madeinbd.com" className="text-orange-600 hover:text-orange-500">payments@madeinbd.com</a> along with your order number.
                      </p>
                    </div>
                  )}
                  
                  {displayOrder.payment_method === 'moneygram' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-700">
                        Please send your payment via Moneygram to the following recipient:
                      </p>
                      <div className="bg-white p-3 rounded-lg border border-yellow-200">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Recipient Name:</span>
                            <span>Made in BD Ltd.</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Country:</span>
                            <span>Bangladesh</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">City:</span>
                            <span>Dhaka</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Amount to Send:</span>
                            <span>${displayOrder.total_amount.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        After sending the payment, please email the Moneygram Reference Number to <a href="mailto:payments@madeinbd.com" className="text-orange-600 hover:text-orange-500">payments@madeinbd.com</a> along with your order number.
                      </p>
                    </div>
                  )}
                  
                  {displayOrder.payment_method === 'bank_transfer' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-700">
                        Please transfer the total amount to the following bank account:
                      </p>
                      <div className="bg-white p-3 rounded-lg border border-yellow-200">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Bank Name:</span>
                            <span>Bangladesh Bank</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Account Name:</span>
                            <span>Made in BD Ltd.</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Account Number:</span>
                            <span>1234567890123</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">SWIFT Code:</span>
                            <span>BBHOBDDHXXX</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Bank Address:</span>
                            <span>Motijheel, Dhaka, Bangladesh</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Amount:</span>
                            <span>${displayOrder.total_amount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Reference:</span>
                            <span>{displayOrder.order_number}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        After making the transfer, please email the transaction details to <a href="mailto:payments@madeinbd.com" className="text-orange-600 hover:text-orange-500">payments@madeinbd.com</a> along with your order number.
                      </p>
                    </div>
                  )}
                  
                  {displayOrder.payment_method === 'escrow' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-700">
                        We use a secure escrow service to protect both buyers and sellers. Here's how it works:
                      </p>
                      <ol className="list-decimal pl-5 space-y-1 text-sm">
                        <li>You'll receive an escrow invitation email within 24 hours</li>
                        <li>Follow the instructions to fund the escrow account</li>
                        <li>We'll ship your order once escrow is funded</li>
                        <li>Inspect your items when they arrive</li>
                        <li>Approve the payment release when satisfied</li>
                      </ol>
                      <p className="text-sm text-gray-700 mt-2">
                        If you have any questions about the escrow process, please contact <a href="mailto:escrow@madeinbd.com" className="text-orange-600 hover:text-orange-500">escrow@madeinbd.com</a>.
                      </p>
                    </div>
                  )}
                  
                  {displayOrder.payment_method === 'letter_of_credit' && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-700">
                        For Letter of Credit (LC) payments, please use the following beneficiary details:
                      </p>
                      <div className="bg-white p-3 rounded-lg border border-yellow-200">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Beneficiary:</span>
                            <span>Made in BD Ltd.</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Bank Name:</span>
                            <span>Bangladesh Bank</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">SWIFT Code:</span>
                            <span>BBHOBDDHXXX</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-700">Bank Address:</span>
                            <span>Motijheel, Dhaka, Bangladesh</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        Our team will contact you within 24 hours to guide you through the LC process. If you have any questions, please contact <a href="mailto:lc@madeinbd.com" className="text-orange-600 hover:text-orange-500">lc@madeinbd.com</a>.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Shipping Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Shipping Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Shipping Address</h3>
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-900">John Smith</p>
                      <p className="text-gray-700">{displayOrder.shipping_address}</p>
                      <p className="text-gray-700">{displayOrder.shipping_city}, {displayOrder.shipping_postal_code}</p>
                      <p className="text-gray-700">{displayOrder.shipping_country}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Shipping Method</h3>
                  <div className="flex items-start space-x-3">
                    <Truck className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-900">{displayOrder.shipping_method}</p>
                      {displayOrder.expected_delivery ? (
                        <p className="text-gray-700">
                          Expected delivery: {new Date(displayOrder.expected_delivery).toLocaleDateString()}
                        </p>
                      ) : (
                        <p className="text-gray-700">
                          Estimated delivery: 7-14 business days
                        </p>
                      )}
                      {displayOrder.tracking_number && (
                        <p className="text-gray-700">
                          Tracking: {displayOrder.tracking_number}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Order Summary</h2>
              
              {/* Price Breakdown */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">
                    ${displayOrder.items.reduce((sum, item) => sum + item.total_price, 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-gray-900">$12.99</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span className="text-gray-900">
                    ${(displayOrder.total_amount - displayOrder.items.reduce((sum, item) => sum + item.total_price, 0) - 12.99).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-3">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="font-semibold text-gray-900">${displayOrder.total_amount.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="space-y-3">
                <Link
                  to="/orders"
                  className="block w-full bg-orange-600 text-white py-2 rounded-lg text-center font-medium hover:bg-orange-700 transition-colors"
                >
                  View All Orders
                </Link>
                <Link
                  to="/"
                  className="block w-full border border-gray-300 text-gray-700 py-2 rounded-lg text-center font-medium hover:bg-gray-50 transition-colors"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
            
            {/* Need Help */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about your order, our customer service team is here to help.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-orange-600" />
                  <span className="text-gray-700">+88 01770 156 736</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-orange-600" />
                  <span className="text-gray-700">support@madeinbd.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-orange-600" />
                  <Link to="/help" className="text-orange-600 hover:text-orange-500">
                    View FAQ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;